import {
  Component,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  UntypedFormControl,
} from '@angular/forms';
import { IMyDate } from '@nodro7/angular-mydatepicker';
import moment, { Moment } from 'moment';
import { DateUtils } from '../date.utils';

@Component({
  selector: 'app-date-time-picker',
  templateUrl: './date-time-picker.component.html',
  styleUrls: ['./date-time-picker.component.css'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => DateTimePickerComponent),
    multi: true,
  }],
})
export class DateTimePickerComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input() name: string = '';
  @Input() placeholder: string = '';
  @Input() disabled: boolean = false;

  @Input() displayFormat: string = 'YYYY-MM-DD HH:mm';

  @Input() dateDisplayFormat: string = 'ddd, D MMM YYYY';

  @Input() dateFormat: string = 'yyyy-mm-dd';

  @Input() minDate: Date | string | Moment | null = null;
  @Input() maxDate: Date | string | Moment | null = null;

  @Input() timeStepMinutes: number = 5;
  @Input() showClearButton: boolean = false;

  @Input() disabledDates: (Date | string | Moment)[] = [];

  @Input() outputFormat: string | null = null;
  @Input() outputUtc: boolean = false;

  dateFormControl: UntypedFormControl;
  timeFormControl: UntypedFormControl;

  internalDisableUntil: IMyDate | null = null;
  internalDisableSince: IMyDate | null = null;

  disabledTimesForSelectedDate: string[] = [];

  private internalValue: Date | null = null;
  private isWritingValue: boolean = false;

  private disabledTimesSetForSelectedDate: Set<string> = new Set<string>();

  private onChangeCallback: (_: any) => void = () => {};
  private onTouchedCallback: () => void = () => {};

  constructor() {
    this.dateFormControl = new UntypedFormControl(null);
    this.timeFormControl = new UntypedFormControl(null);

    this.dateFormControl.valueChanges.subscribe(() => this.handleInnerChange());
    this.timeFormControl.valueChanges.subscribe(() => this.handleInnerChange());
  }

  ngOnInit(): void {
    this.syncDisabledDates();
    this.refreshDisabledTimesForSelectedDate(this.dateFormControl.value);
    this.applyDisabledInputState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['minDate'] || changes['maxDate']) {
      this.syncDisabledDates();
    }

    if (changes['disabledDates'] || changes['timeStepMinutes'] || changes['minDate'] || changes['maxDate']) {
      this.refreshDisabledTimesForSelectedDate(this.dateFormControl.value);

      if (!this.isWritingValue && this.dateFormControl.value) {
        this.reconcileCurrentTimeWithDisabledTimes();
      }
    }

    if (changes['disabled']) {
      this.applyDisabledInputState();
    }
  }

  writeValue(rawValue: any): void {
    this.isWritingValue = true;

    try {
      if (rawValue === null || rawValue === undefined || rawValue === '') {
        this.internalValue = null;
        this.dateFormControl.setValue(null, { emitEvent: false });
        this.timeFormControl.setValue(null, { emitEvent: false });
        this.refreshDisabledTimesForSelectedDate(null);
        return;
      }

      let m: Moment | null = null;

      if (rawValue instanceof Date) {
        m = moment(rawValue);
      } else if (moment.isMoment(rawValue)) {
        m = rawValue as Moment;
      } else if (typeof rawValue === 'string') {
        if (this.outputFormat) {
          m = moment(rawValue, this.outputFormat, true);
          if (!m.isValid()) {
            m = moment(rawValue);
          }
        } else {
          m = moment(rawValue);
        }
      }

      if (!m || !m.isValid()) {
        this.internalValue = null;
        this.dateFormControl.setValue(null, { emitEvent: false });
        this.timeFormControl.setValue(null, { emitEvent: false });
        this.refreshDisabledTimesForSelectedDate(null);
        return;
      }

      const jsDate = m.toDate();
      this.internalValue = jsDate;

      this.dateFormControl.setValue(jsDate, { emitEvent: false });
      this.timeFormControl.setValue(m.format('HH:mm'), { emitEvent: false });

      this.refreshDisabledTimesForSelectedDate(jsDate);
      this.reconcileCurrentTimeWithDisabledTimes();
    } finally {
      this.isWritingValue = false;
    }
  }

  registerOnChange(fn: (_: any) => {}): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.applyDisabledInputState();
  }

  onClearClicked(): void {
    if (this.disabled) {
      return;
    }

    this.isWritingValue = true;
    try {
      this.dateFormControl.setValue(null, { emitEvent: false });
      this.timeFormControl.setValue(null, { emitEvent: false });
      this.internalValue = null;
      this.refreshDisabledTimesForSelectedDate(null);
      this.propagateChange(null);
    } finally {
      this.isWritingValue = false;
    }
  }

  private handleInnerChange(): void {
    if (this.isWritingValue || this.disabled) {
      return;
    }

    const dateValue: Date | null = this.dateFormControl.value;
    let timeValue: string | null = this.timeFormControl.value;

    this.refreshDisabledTimesForSelectedDate(dateValue);

    if (!dateValue) {
      this.internalValue = null;
      this.propagateChange(null);
      return;
    }

    const bestTimeValue = this.getBestTimeValue(timeValue);

    if (bestTimeValue !== timeValue) {
      this.isWritingValue = true;
      try {
        this.timeFormControl.setValue(bestTimeValue, { emitEvent: false });
        timeValue = bestTimeValue;
      } finally {
        this.isWritingValue = false;
      }
    }

    if (!timeValue) {
      this.internalValue = null;
      this.propagateChange(null);
      return;
    }

    const [hoursStr, minutesStr] = timeValue.split(':');
    const hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);

    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      this.internalValue = null;
      this.propagateChange(null);
      return;
    }

    let combinedMoment = moment(dateValue)
      .hours(hours)
      .minutes(minutes)
      .seconds(0)
      .milliseconds(0);

    combinedMoment = this.clampToMinMax(combinedMoment);

    if (!combinedMoment.isValid()) {
      this.internalValue = null;
      this.propagateChange(null);
      return;
    }

    let finalMoment = combinedMoment;

    if (this.outputUtc) {
      finalMoment = combinedMoment.utc(true);
    }

    const finalDate = finalMoment.toDate();
    this.internalValue = finalDate;

    this.isWritingValue = true;
    try {
      this.dateFormControl.setValue(finalDate, { emitEvent: false });
    } finally {
      this.isWritingValue = false;
    }

    this.propagateChange(finalDate);
  }

  private propagateChange(dateValue: Date | null): void {
    let emitted: any = null;

    if (dateValue) {
      emitted = this.outputFormat ? moment(dateValue).format(this.outputFormat) : dateValue;
    }

    this.onChangeCallback(emitted);
    this.onTouchedCallback();
  }

  private applyDisabledInputState(): void {
    if (this.disabled) {
      this.dateFormControl.disable({ emitEvent: false });
      this.timeFormControl.disable({ emitEvent: false });
    } else {
      this.dateFormControl.enable({ emitEvent: false });
      this.timeFormControl.enable({ emitEvent: false });
    }
  }

  private syncDisabledDates(): void {
    const minMoment = this.toMoment(this.minDate);
    const maxMoment = this.toMoment(this.maxDate);

    this.internalDisableUntil = minMoment
      ? DateUtils.convertDateMomentToPickerFormat(minMoment.clone().startOf('day').subtract(1, 'day'))
      : null;

    this.internalDisableSince = maxMoment
      ? DateUtils.convertDateMomentToPickerFormat(maxMoment.clone().startOf('day').add(1, 'day'))
      : null;
  }

  private refreshDisabledTimesForSelectedDate(dateValue: Date | null): void {
    if (!dateValue) {
      this.disabledTimesSetForSelectedDate = new Set<string>();
      this.disabledTimesForSelectedDate = [];
      return;
    }

    const step = Math.max(1, Math.floor(this.timeStepMinutes));
    const options = this.buildAllTimeOptions(step);
    const selectedDay = moment(dateValue).startOf('day');

    const disabledSet = new Set<string>();

    const minMoment = this.toMoment(this.minDate);
    if (minMoment && minMoment.isValid() && minMoment.clone().startOf('day').isSame(selectedDay, 'day')) {
      const earliestAllowedMinutes = this.roundMinutesUpToStep(this.minutesSinceMidnight(minMoment), step);
      for (const option of options) {
        const optionMinutes = this.minutesSinceMidnightFromString(option);
        if (optionMinutes !== null && optionMinutes < earliestAllowedMinutes) {
          disabledSet.add(option);
        }
      }
    }

    const maxMoment = this.toMoment(this.maxDate);
    if (maxMoment && maxMoment.isValid() && maxMoment.clone().startOf('day').isSame(selectedDay, 'day')) {
      const latestAllowedMinutes = this.roundMinutesDownToStep(this.minutesSinceMidnight(maxMoment), step);
      for (const option of options) {
        const optionMinutes = this.minutesSinceMidnightFromString(option);
        if (optionMinutes !== null && optionMinutes > latestAllowedMinutes) {
          disabledSet.add(option);
        }
      }
    }

    if (Array.isArray(this.disabledDates)) {
      for (const value of this.disabledDates) {
        const m = this.toMoment(value);
        if (!m || !m.isValid()) {
          continue;
        }

        if (!m.clone().startOf('day').isSame(selectedDay, 'day')) {
          continue;
        }

        disabledSet.add(this.formatTimeRoundedDownToStep(m, step));
      }
    }

    this.disabledTimesSetForSelectedDate = disabledSet;
    this.disabledTimesForSelectedDate = Array.from(disabledSet);
  }

  private reconcileCurrentTimeWithDisabledTimes(): void {
    const best = this.getBestTimeValue(this.timeFormControl.value);
    if (best === this.timeFormControl.value) {
      return;
    }

    this.isWritingValue = true;
    try {
      this.timeFormControl.setValue(best, { emitEvent: false });
    } finally {
      this.isWritingValue = false;
    }
  }

  private getBestTimeValue(currentTimeValue: string | null): string | null {
    const step = Math.max(1, Math.floor(this.timeStepMinutes));
    const options = this.buildAllTimeOptions(step);

    const current = (typeof currentTimeValue === 'string' && currentTimeValue.length > 0)
      ? currentTimeValue
      : null;

    if (current && !this.disabledTimesSetForSelectedDate.has(current)) {
      return current;
    }

    for (const option of options) {
      if (!this.disabledTimesSetForSelectedDate.has(option)) {
        return option;
      }
    }

    return null;
  }

  private clampToMinMax(value: Moment): Moment {
    const minMoment = this.toMoment(this.minDate);
    if (minMoment && minMoment.isValid() && value.isBefore(minMoment)) {
      const step = Math.max(1, Math.floor(this.timeStepMinutes));
      const minutes = this.roundMinutesUpToStep(this.minutesSinceMidnight(minMoment), step);
      return minMoment.clone().startOf('day').add(minutes, 'minutes').seconds(0).milliseconds(0);
    }

    const maxMoment = this.toMoment(this.maxDate);
    if (maxMoment && maxMoment.isValid() && value.isAfter(maxMoment)) {
      const step = Math.max(1, Math.floor(this.timeStepMinutes));
      const minutes = this.roundMinutesDownToStep(this.minutesSinceMidnight(maxMoment), step);
      return maxMoment.clone().startOf('day').add(minutes, 'minutes').seconds(0).milliseconds(0);
    }

    return value;
  }

  private toMoment(value: any): Moment | null {
    if (!value) {
      return null;
    }

    if (moment.isMoment(value)) {
      return value as Moment;
    }

    if (value instanceof Date) {
      return moment(value);
    }

    if (typeof value === 'string') {
      return moment(value);
    }

    return null;
  }

  private buildAllTimeOptions(stepMinutes: number): string[] {
    const step = Math.max(1, Math.floor(stepMinutes));
    const options: string[] = [];

    for (let hour = 0; hour <= 23; hour++) {
      for (let minutes = 0; minutes < 60; minutes += step) {
        const hourStr = hour.toString().padStart(2, '0');
        const minuteStr = minutes.toString().padStart(2, '0');
        options.push(`${hourStr}:${minuteStr}`);
      }
    }

    return options;
  }

  private minutesSinceMidnight(m: Moment): number {
    return (m.hours() * 60) + m.minutes();
  }

  private minutesSinceMidnightFromString(timeValue: string): number | null {
    const parts = timeValue.split(':');
    if (parts.length !== 2) {
      return null;
    }

    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);

    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return null;
    }

    return (hours * 60) + minutes;
  }

  private roundMinutesUpToStep(minutes: number, step: number): number {
    const safeStep = Math.max(1, Math.floor(step));
    return Math.min(23 * 60 + 59, Math.ceil(minutes / safeStep) * safeStep);
  }

  private roundMinutesDownToStep(minutes: number, step: number): number {
    const safeStep = Math.max(1, Math.floor(step));
    return Math.max(0, Math.floor(minutes / safeStep) * safeStep);
  }

  private formatTimeRoundedDownToStep(m: Moment, step: number): string {
    const minutes = this.roundMinutesDownToStep(this.minutesSinceMidnight(m), step);
    const hh = Math.floor(minutes / 60).toString().padStart(2, '0');
    const mm = (minutes % 60).toString().padStart(2, '0');
    return `${hh}:${mm}`;
  }

  get timeStepSeconds(): number {
    return this.timeStepMinutes * 60;
  }

  get combinedDisplay(): string | null {
    if (!this.internalValue) {
      return null;
    }

    const m = moment(this.internalValue);
    return m.isValid() ? m.format(this.displayFormat) : null;
  }
}
